import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { TaskTemplateDto } from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from '../components/common/ConfirmDialog'
import { EmptyState, ErrorAlert, LoadingBlock } from '../components/common/Feedback'
import { PageHeader } from '../components/common/PageHeader'
import { ApplyTemplateDialog } from '../components/tasks/ApplyTemplateDialog'
import { TemplateEditorDialog } from '../components/templates/TemplateEditorDialog'
import { useTemplates } from '../hooks/useLookups'
import { api } from '../lib/api'
import { qk } from '../lib/queryClient'

export function TemplatesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data, isPending, error, refetch } = useTemplates()
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<TaskTemplateDto | null>(null)
  const [applyOpen, setApplyOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<TaskTemplateDto | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/task-templates/${id}`),
    onSuccess: () => {
      setPendingDelete(null)
      void queryClient.invalidateQueries({ queryKey: qk.templates })
    },
  })

  return (
    <>
      <PageHeader
        title="תבניות משימות"
        subtitle="רשימות משימות מוגדרות מראש. הפעלת תבנית יוצרת עותק — אין קשר חי בין המשימות לתבנית."
        actions={
          <>
            <Button startIcon={<PlaylistAddIcon />} onClick={() => setApplyOpen(true)}>
              יצירת משימות מתבנית
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditing(null)
                setEditorOpen(true)
              }}
            >
              תבנית חדשה
            </Button>
          </>
        }
      />

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {remove.error ? <ErrorAlert error={remove.error} /> : null}
      {isPending ? <LoadingBlock /> : null}

      {data ? (
        data.length === 0 ? (
          <EmptyState title="אין תבניות" hint="תבנית מאגדת רשימת משימות שחוזרת על עצמה" />
        ) : (
          <Stack spacing={1.5}>
            {data.map((template) => {
              const isOpen = expanded === template.id
              return (
                <Paper key={template.id} variant="outlined">
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                    <Tooltip title={isOpen ? 'סגירה' : 'הצגת הפריטים'}>
                      <IconButton
                        size="small"
                        onClick={() => setExpanded(isOpen ? null : template.id)}
                        sx={{
                          transform: isOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 150ms',
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography>{template.name}</Typography>
                      {template.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {template.description}
                        </Typography>
                      ) : null}
                    </Box>

                    <Chip size="small" label={`${template.items.length} פריטים`} />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditing(template)
                        setEditorOpen(true)
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setPendingDelete(template)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Collapse in={isOpen} unmountOnExit>
                    <Divider />
                    <Stack spacing={0.5} sx={{ px: 3, py: 2 }}>
                      {template.items.map((item, index) => (
                        <Typography key={item.id} variant="body2">
                          {index + 1}. {item.name}
                          {item.description ? ` — ${item.description}` : ''}
                          {item.dueOffsetDays !== null ? ` (יעד: ${item.dueOffsetDays} ימים מההתחלה)` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  </Collapse>
                </Paper>
              )
            })}
          </Stack>
        )
      ) : null}

      <TemplateEditorDialog
        open={editorOpen}
        template={editing}
        onClose={() => setEditorOpen(false)}
      />

      <ApplyTemplateDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onApplied={(parentTaskId) => navigate(`/tasks?taskId=${parentTaskId}`)}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="מחיקת תבנית"
        message={`התבנית "${pendingDelete?.name ?? ''}" תימחק. משימות שנוצרו ממנה יישארו במערכת.`}
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />
    </>
  )
}
